import { enableProdMode } from '@angular/core';
import { platformBrowserDynamic } from '@angular/platform-browser-dynamic';

import { AppModule } from './app/app.module';
import { environment } from './environments/environment';
import { msalInstance } from './app/config/msal.config';
import { isInteractionRequiredError } from './app/shared/msal-error.utils';
import { clearAuthCookies, clearAuthSessionFlags } from './app/shared/auth-cleanup';

if (environment.production) {
  enableProdMode();
}

async function redirectToLogin(reason: string): Promise<void> {
  console.warn('[MAIN] MSAL session unrecoverable, redirecting to /login:', reason);
  try {
    await msalInstance.clearCache();
  } catch {
    msalInstance.setActiveAccount(null);
  }
  clearAuthCookies();
  clearAuthSessionFlags();
  window.location.replace('/login');
}

/**
 * Workaround per bug noto B2C: subito dopo handleRedirectPromise il primo
 * acquireTokenSilent puo` ritornare AuthenticationResult con accessToken=""
 * (cfr. stringa "Known issue with B2C tenants" hardcoded in @azure/msal-angular).
 * MsalInterceptor in quel caso lascia partire la richiesta con header "Bearer "
 * vuoto -> backend risponde 401.
 *
 * Pre-popoliamo la cache SEQUENZIALMENTE prima di bootstrapare AppModule:
 * questo elimina anche la race condition tra le prime chiamate HTTP che
 * competono per lo stesso refresh token. Gli scope DEVONO includere openid +
 * profile + offline_access, altrimenti B2C non emette l'access token nel
 * primo silent acquire.
 *
 * Ritorna 'redirect-required' se MSAL segnala che e` necessario un login
 * interattivo (refresh token scaduto -> AADB2C90085 invalid_grant, silent
 * iframe bloccato da X-Frame-Options: deny). In quel caso il caller NON
 * deve bootstrappare AppModule: l'app si avvierebbe in stato zombie (account
 * in cache MSAL, niente Bearer, ma backend autorizza via cookie
 * .AspNet.Cookies -> nessun 401 -> nessun redirect a /login mai). Vedi
 * 2026-05-27-amsuite-msal-skip-popup-design.md.
 */
async function prewarmTokenCache(): Promise<'ok' | 'redirect-required'> {
  const account = msalInstance.getActiveAccount();
  if (!account) return 'ok';

  const scopes = ['openid', 'profile', 'offline_access', environment.msalApiScope];
  try {
    let result = await msalInstance.acquireTokenSilent({ scopes, account });
    if (!result?.accessToken) {
      result = await msalInstance.acquireTokenSilent({ scopes, account, forceRefresh: true });
    }
    if (!result?.accessToken) {
      console.warn('[MAIN] Token pre-warm completed but accessToken is still empty');
    }
    return 'ok';
  } catch (e) {
    if (isInteractionRequiredError(e)) {
      return 'redirect-required';
    }
    console.warn('[MAIN] Token pre-warm failed (interceptor will retry):', e);
    return 'ok';
  }
}

function isOnLoginRoute(): boolean {
  const path = window.location.pathname;
  return path === '/login' || path.startsWith('/login/');
}

msalInstance.initialize()
  .then(() => msalInstance.handleRedirectPromise())
  .then(async (redirectResponse) => {
    if (redirectResponse?.account) {
      msalInstance.setActiveAccount(redirectResponse.account);
    } else {
      const accounts = msalInstance.getAllAccounts();
      if (accounts.length > 0 && !msalInstance.getActiveAccount()) {
        msalInstance.setActiveAccount(accounts[0]);
      }
    }

    const prewarmStatus = await prewarmTokenCache();
    if (prewarmStatus === 'redirect-required' && !isOnLoginRoute()) {
      await redirectToLogin('prewarm InteractionRequiredAuthError');
      return;
    }

    platformBrowserDynamic().bootstrapModule(AppModule)
      .catch(err => console.error('[MAIN] Bootstrap ERROR:', err));
  })
  .catch(err => {
    console.error('[MAIN] MSAL initialization failed:', err);
    platformBrowserDynamic().bootstrapModule(AppModule)
      .catch(bootstrapErr => console.error('[MAIN] Bootstrap ERROR:', bootstrapErr));
  });
